import { rateAlarmEntries } from '../../utils/feedingRates';
// utils/exportSummaryPDF.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Chart from 'chart.js/auto';

import Itg from './ITG_BLANCO.png'  
import ItgNegro from './ITG-Logo-Negro.png'  
import Scapp from './logo_scapp_blanco.png'  
import ScappNegro from './logo_scapp_negro.png'  


const loadImageBase64 = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.src = src;
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = img.width; c.height = img.height;
    c.getContext('2d').drawImage(img, 0, 0);
    resolve(c.toDataURL('image/png'));
  };
  img.onerror = reject;
});

// toma primer color de un dataset (si es array, el 1ro)
const firstColor = (c) => Array.isArray(c) ? (c[0] || '#a1a1a1') : (c || '#a1a1a1');

function rgbString([r,g,b]) { return `rgb(${r}, ${g}, ${b})`; }

function forceOpaqueColor(c) {
  const [r,g,b] = toRGB(c);
  return rgbString([r,g,b]);              // sin alfa
}

// opcional: iluminar un poco en dark
function lightenRGB([r,g,b], amt=0.12) {
  const f = (x)=> Math.min(255, Math.round(x + (255 - x)*amt));
  return [f(r), f(g), f(b)];
}
function forceOpaqueAndMaybeLighten(c, dark=false) {
  let rgb = toRGB(c);
  if (dark) rgb = lightenRGB(rgb, 0.12); // sube ~12% en dark
  return rgbString(rgb);
}

// Convierte a [r,g,b] sin alfa (evita error "Invalid color ... rgba(...)")
function toRGB(color) {
  if (!color) return [0, 0, 0];
  if (Array.isArray(color)) {
    const arr = color.slice(0, 3).map(n => Math.max(0, Math.min(255, Math.round(Number(n) || 0))));
    return [arr[0] || 0, arr[1] || 0, arr[2] || 0];
  }
  if (typeof color === 'string') {
    const s = color.trim();
    if (s.startsWith('#')) {
      let h = s.slice(1);
      if (h.length === 3) h = h.split('').map(ch => ch + ch).join('');
      const num = parseInt(h, 16);
      return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
    }
    if (s.startsWith('rgba')) {
      const [r, g, b] = s.replace(/^rgba?\(|\)$/g, '').split(',').map(v => parseFloat(v.trim()));
      return [Math.round(r || 0), Math.round(g || 0), Math.round(b || 0)];
    }
    if (s.startsWith('rgb')) {
      const [r, g, b] = s.replace(/^rgba?\(|\)$/g, '').split(',').map(v => parseFloat(v.trim()));
      return [Math.round(r || 0), Math.round(g || 0), Math.round(b || 0)];
    }
  }
  return [0, 0, 0];
}

// setTextColor seguro para jsPDF (acepta hex/rgb/rgba/array)
function setTextColorSafe(pdf, color) {
  const [r, g, b] = toRGB(color);
  pdf.setTextColor(r, g, b);
}

/* Renderiza un Chart.js en un canvas temporal y devuelve su PNG */
async function renderTempChartImage({ type, data, options, width = 1600, height = 600 }) {
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');

  const chart = new Chart(ctx, {
    type,
    data,
    options: { responsive: false, animation: false, ...options },
  });

  // Garantiza que termine el primer render
  await new Promise(r => setTimeout(r, 50));
  const png = canvas.toDataURL('image/png', 1.0);
  chart.destroy();
  return { png, width, height };
}

function parseDateSafe(s) {
  if (!s || typeof s !== 'string') return null;
  if (s.includes('/')) { const [d,m,y] = s.split('/'); return new Date(`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`); }
  if (s.includes('-')) { const [y,m,d] = s.split('-'); return new Date(`${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`); }
  return null;
}
function sameDay(a,b) {
  const da = parseDateSafe(a), db = parseDateSafe(b);
  return da && db && da.getTime() === db.getTime();
}

export function collectDonutDetails({ jaulas=[], jaulaSeleccionada=null, selectedDay=null, selectedRateKey='auto' }) {
  const chosen = jaulaSeleccionada
    ? jaulas.filter(j => j.name === jaulaSeleccionada)
    : jaulas;

  const cats = [
    { key:'baja',  label:'Tasa Baja'  },
    { key:'media', label:'Tasa Media' },
    { key:'alta',  label:'Tasa Alta'  },
  ];

  const perCat = {};
  for (const {key,label} of cats) {
    const map = new Map(); // tasa -> Set(jaulas)
    let alarmsTotal = 0;

    for (const j of chosen) {
      const arr = rateAlarmEntries(j, selectedRateKey);
      for (const it of arr) {
        if (selectedDay && !sameDay(it.day, selectedDay)) continue;
        const val = it?.value?.[key];
        if (!val) continue;
        alarmsTotal += Number(val.value || 0);
        const tasas = Array.isArray(val.tasas) ? val.tasas : [];
        for (const t of tasas) {
          if (typeof t !== 'number' || t <= 0) continue;
          const identity = `${it.unit}|${t}`;
          if (!map.has(identity)) map.set(identity, { tasa: t, unit: it.unit, names: new Set() });
          map.get(identity).names.add(j.name);
        }
      }
    }

    const rows = Array.from(map.values())
      .sort((a,b) => a.tasa - b.tasa)
      .map(({tasa, unit, names}) => [
        (tasa % 1 !== 0) ? tasa.toFixed(2) : tasa,
        unit,
        Array.from(names).sort((a,b)=>String(a).localeCompare(String(b),undefined,{numeric:true,sensitivity:'base'})).join(', ')
      ]);

    perCat[key] = { label, alarmsTotal, rows };
  }
  return perCat;
}


/* ================= export principal ================= */
export async function exportSummaryPDF({
  barChart,                 // instancia Chart.js (solo para leer .config.data)
  donutChart,               // instancia Chart.js (solo para leer .config.data)
  barDataOverride,          // opcional: { labels, datasets[] } si prefieres pasar data directa
  donutDataOverride,        // opcional: idem para dona
  darkModePDF = false,
  fileName = 'Resumen.pdf',
  titles = { bar: 'Resumen ', donut: 'Alarmas por Tasa ' },
  donutDetailSource,
  onProgress,               // 👈 callback de progreso (0..100)
}) {
  try {
    onProgress?.(1);

    /* 1) Datos */
    const barData = barDataOverride || (barChart?.config?.data ?? {});
    const donutData = donutDataOverride || (donutChart?.config?.data ?? {});
    if (!barData?.labels || !barData?.datasets) { console.error('Faltan datos de barras'); onProgress?.(100); return; }
    if (!donutData?.datasets?.[0]?.data) { console.error('Faltan datos de dona'); onProgress?.(100); return; }

    /* 2) Logos */
    const itg = Itg
    const itgNegro = ItgNegro
    const scapp = Scapp
    const scappNegro = ScappNegro
    const logoITGPath = darkModePDF ? itg : itgNegro;
    const logoSCAPPPath = darkModePDF ? scapp : scappNegro;

    const [logoITG, logoSCAPP] = await Promise.all([
      loadImageBase64(logoITGPath),
      loadImageBase64(logoSCAPPPath),
    ]);
    onProgress?.(10);

    /* 3) PDF base */
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const PAD = 12, HEADER_H = 60;

    const bg = darkModePDF ? '#1e1e1e' : '#ffffff';
    const text = darkModePDF ? '#ffffff' : '#000000';
    const grid = darkModePDF ? '#444444' : 'rgba(0,0,0,0.3)';
    const axisBase = darkModePDF ? '#ffffff' : '#000000';

    const paintBG = () => { pdf.setFillColor(bg); pdf.rect(0, 0, pageW, pageH, 'F'); };
    const paintHeader = () => {
      const itgW = 70, scW = 120;
      const itgY = (HEADER_H - 40) / 2;
      const scY = (HEADER_H - 30) / 2 + 9;
      pdf.addImage(logoITG, 'PNG', 40, itgY, itgW, 0);
      pdf.addImage(logoSCAPP, 'PNG', pageW - scW - 40, scY, scW, 0);
    };
    const pageStart = (title) => {
      paintBG(); paintHeader();
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(16);
      setTextColorSafe(pdf, text);
      pdf.text(title, pageW / 2, 75, { align: 'center' });
    };

    /* 4) Colores tablas (modo dark) */
    const DARK_BODY = [43, 43, 43];
    const DARK_ALT  = [36, 36, 36];
    const DARK_BORDER = [160, 160, 160];
    const LIGHT_BODY = [255, 255, 255];
    const LIGHT_ALT  = [245, 245, 245];
    const ORANGE = [232, 78, 14];

    /* ============ PÁGINA 1: GRÁFICO DE BARRAS ============ */
    pageStart(titles.bar);

    // Colores por eje desde datasets
    const axisColors = { 'y-left': axisBase, 'y-right-ton': axisBase, 'y-right-count': axisBase };
    barData.datasets.forEach(ds => {
      const id = ds.yAxisID || 'y-left';
      axisColors[id] = firstColor(ds.backgroundColor);
    });

    // 3 ejes: SFR izquierda; los otros dos a la derecha
    const scalesForBar = {
      x: {
        title: { display: true, text: 'Jaulas', color: axisBase, font: { size: 12, weight: 'bold' } },
        ticks: { color: axisBase },
        grid: { color: grid },
      },
      'y-left': {
        type: 'linear',
        position: 'left',
        min: 0,
        suggestedMin: 0,
        ticks: { color: axisColors['y-left'], beginAtZero: true },
        grid: { color: grid },
        title: { display: false, text: 'SFR', color: axisColors['y-left'] },
      },
      'y-right-ton': {
        type: 'linear',
        position: 'right',
        min: 0,
        suggestedMin: 0,
        ticks: { color: axisColors['y-right-ton'], beginAtZero: true, padding: 6 },
        grid: { drawOnChartArea: false, color: grid },
        title: { display: false, text: 'Alarms/Ton o /Visits', color: axisColors['y-right-ton'] },
      },
      'y-right-count': {
        type: 'linear',
        position: 'right',
        min: 0,
        suggestedMin: 0,
        ticks: { color: axisColors['y-right-count'], beginAtZero: true, padding: 6 },
        grid: { drawOnChartArea: false, color: grid },
        title: { display: false, text: 'Alarms', color: axisColors['y-right-count'] },
      },
    };

    const { png: barPNG, width: barW, height: barH } = await renderTempChartImage({
      type: 'bar',
      data: barData,
      options: { plugins: { legend: { display: true, labels: { color: text } }, tooltip: { enabled: false } }, scales: scalesForBar },
      width: 1600,
      height: 700,
    });
    onProgress?.(35);

    // marco + imagen del gráfico de barras
    const gX = pageW * 0.1;
    const gW = pageW * 0.8;
    const gH = gW * (barH / barW);
    const gY = 110;

    pdf.setFillColor(darkModePDF ? '#2b2b2b' : '#f5f5f5');
    pdf.setDrawColor(darkModePDF ? '#444' : '#ccc');
    pdf.roundedRect(gX - PAD, gY - PAD, gW + PAD * 2, gH + PAD * 2, 6, 6, 'FD');
    pdf.addImage(barPNG, 'PNG', gX, gY, gW, gH);

    // =================== TABLAS de BARRAS ===================
    const labelsBar = barData.labels || [];
    const setsBar   = barData.datasets || [];

    const ROWS_PER_TABLE     = 6;
    const MAX_COLS_PER_PAGE  = 5;
    const marginX            = 40;
    const gapX               = 12;
    const GROUP_GAP_Y        = 20;
    const bottomMargin       = 40;

    let startY = gY + gH + 45;

    const estimateTableHeight = (rows) => 18 + rows * 12 + 10;
    const minNeededHeight = 10 + estimateTableHeight(ROWS_PER_TABLE);

    if (startY + minNeededHeight > pageH - bottomMargin) {
      pdf.addPage(); pageStart(`${titles.bar} — Valores`);
      startY = HEADER_H + 28;
    }

    // progreso proporcional (35 → 70) según cantidad total de tablas
    const totalBarChunks =
      setsBar.reduce((acc) => acc + Math.ceil((labelsBar.length || 0) / ROWS_PER_TABLE), 0) || 1;
    let processedBarChunks = 0;

    for (let s = 0; s < setsBar.length; s++) {
      const ds = setsBar[s];

      // título del grupo con color del dataset
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
      setTextColorSafe(pdf, firstColor(ds.backgroundColor) || text);
      pdf.text(ds.label || `Serie ${s + 1}`, marginX, startY);
      setTextColorSafe(pdf, text);
      startY += 10;

      const headBar = [['Jaula', 'Valor']];
      const bodyAll = labelsBar.map((lbl, i) => {
        const v = Array.isArray(ds.data) ? ds.data[i] : undefined;
        const val = (typeof v === 'number' && !Number.isInteger(v)) ? v.toFixed(2) : (v ?? '');
        return [lbl, val];
      });

      const chunks = [];
      for (let i = 0; i < bodyAll.length; i += ROWS_PER_TABLE) chunks.push(bodyAll.slice(i, i + ROWS_PER_TABLE));
      let remaining = [...chunks];

      while (remaining.length) {
        const colsThis = Math.min(MAX_COLS_PER_PAGE, remaining.length);
        const totalW = pageW - marginX * 2 - gapX * (colsThis - 1);
        const tableW = totalW / colsThis;

        if (startY + estimateTableHeight(ROWS_PER_TABLE) > pageH - bottomMargin) {
          pdf.addPage(); pageStart(`${titles.bar} — Valores`);
          startY = HEADER_H + 28;

          // reimprime el título del grupo
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
          setTextColorSafe(pdf, firstColor(ds.backgroundColor) || text);
          pdf.text(ds.label || `Serie ${s + 1}`, marginX, startY);
          setTextColorSafe(pdf, text);
          startY += 10;
        }

        const colBottomYs = [];
        let x = marginX;

        for (let c = 0; c < colsThis; c++) {
          const body = remaining.shift();

          autoTable(pdf, {
            head: headBar,
            body,
            startY,
            margin: { left: x, right: marginX, top: 0, bottom: 0 },
            tableWidth: tableW,
            theme: 'grid',
            pageBreak: 'avoid',
            styles: {
              fontSize: 8, halign: 'center', valign: 'middle',
              lineWidth: 0.25, lineColor: darkModePDF ? DARK_BORDER : [0, 0, 0],
              textColor: darkModePDF ? [255, 255, 255] : [0, 0, 0],
              fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY,
              cellPadding: { top: 3, bottom: 3 },
            },
            headStyles: { fillColor: ORANGE, textColor: [255, 255, 255], halign: 'center' },
            bodyStyles: { fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY },
            alternateRowStyles: { fillColor: darkModePDF ? DARK_ALT : LIGHT_ALT },
          });

          colBottomYs.push(pdf.lastAutoTable.finalY);

          // progreso proporcional
          processedBarChunks++;
          const pct = 35 + Math.round((processedBarChunks / totalBarChunks) * 35);
          onProgress?.(Math.min(pct, 70));

          x += tableW + gapX;
        }

        startY = Math.max(...colBottomYs) + GROUP_GAP_Y;

        if (remaining.length && startY + estimateTableHeight(ROWS_PER_TABLE) > pageH - bottomMargin) {
          pdf.addPage(); pageStart(`${titles.bar} — Valores`);
          startY = HEADER_H + 28;

          // reimprime título del grupo
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
          setTextColorSafe(pdf, firstColor(ds.backgroundColor) || text);
          pdf.text(ds.label || `Serie ${s + 1}`, marginX, startY);
          setTextColorSafe(pdf, text);
          startY += 10;
        }
      }

      // línea separadora + gap entre grupos
      pdf.setDrawColor(darkModePDF ? 90 : 210);
      pdf.setLineWidth(0.3);
      pdf.line(marginX, startY - GROUP_GAP_Y / 2, pageW - marginX, startY - GROUP_GAP_Y / 2);
      startY += GROUP_GAP_Y;

      if (s < setsBar.length - 1 && startY + minNeededHeight > pageH - bottomMargin) {
        pdf.addPage(); pageStart(`${titles.bar} — Valores`);
        startY = HEADER_H + 28;
      }
    }

    /* ============ SIGUIENTE: GRÁFICO DE DONA + TABLAS ============ */
    pdf.addPage(); pageStart(titles.donut);

    const donutOptions = {
      plugins: {
        legend: { display: true, position: 'right', labels: { color: text, boxWidth: 18 } },
        tooltip: { enabled: false },
      },
      cutout: '60%',
    };

    // DONUT con colores opacos
    const solidDonutData = {
      labels: donutData.labels,
      datasets: (donutData.datasets || []).map(ds => ({
        ...ds,
        backgroundColor: Array.isArray(ds.backgroundColor)
          ? ds.backgroundColor.map(c => forceOpaqueAndMaybeLighten(c, darkModePDF))
          : forceOpaqueAndMaybeLighten(ds.backgroundColor, darkModePDF),
        borderColor: Array.isArray(ds.borderColor)
          ? ds.borderColor.map(c => forceOpaqueColor(c))
          : forceOpaqueColor(ds.borderColor || '#000'),
        borderWidth: ds.borderWidth ?? 1
      })),
    };

    const { png: donutPNG, width: dW, height: dH } = await renderTempChartImage({
      type: 'doughnut',
      data: solidDonutData,
      options: donutOptions,
      width: 1200,
      height: 600,
    });
    onProgress?.(80);

    const dImgW = pageW * 0.8;
    const dImgH = dImgW * (dH / dW);
    const dX = (pageW - dImgW) / 2;
    const dY = 110;

    pdf.setFillColor(darkModePDF ? '#2b2b2b' : '#f5f5f5');
    pdf.setDrawColor(darkModePDF ? '#444' : '#ccc');
    pdf.roundedRect(dX - PAD, dY - PAD, dImgW + PAD * 2, dImgH + PAD * 2, 6, 6, 'FD');
    pdf.addImage(donutPNG, 'PNG', dX, dY, dImgW, dImgH);

    // Tabla de dona (Categoría | Valor | %)
    const labelsDonut = donutData.labels || [];
    const ds0 = donutData.datasets?.[0] || { data: [], backgroundColor: [] };
    const dataDonut = Array.isArray(ds0.data) ? ds0.data : [];
    const totalDonut = dataDonut.reduce((a, b) => a + (Number(b) || 0), 0);
    const donutRows = labelsDonut.map((lbl, i) => {
      const v = Number(dataDonut[i] || 0);
      const pct = totalDonut > 0 ? `${((v / totalDonut) * 100).toFixed(1)}%` : '0%';
      return [lbl, v, pct];
    });

    autoTable(pdf, {
      head: [['Categoría', 'Alarmas', '%']],
      body: donutRows,
      startY: dY + dImgH + 24,
      margin: { left: 40, right: 40 },
      tableWidth: pageW - 80,
      theme: 'grid',
      styles: {
        fontSize: 9, halign: 'center', valign: 'middle',
        lineWidth: 0.25, lineColor: darkModePDF ? DARK_BORDER : [0, 0, 0],
        textColor: darkModePDF ? [255, 255, 255] : [0, 0, 0],
        fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY,
      },
      headStyles: { fillColor: ORANGE, textColor: [255, 255, 255], halign: 'center' },
      bodyStyles: { fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY },
      alternateRowStyles: { fillColor: darkModePDF ? DARK_ALT : LIGHT_ALT },
    });
    onProgress?.(85);

    // ---------- Detalle por categoría (con continuaciones en columnas) ----------
    if (donutDetailSource) {
      const dset0 = solidDonutData.datasets?.[0] || donutData.datasets?.[0] || {};
      const colorForLabel = (lbl) => {
        const idx = (solidDonutData.labels || donutData.labels || []).findIndex(
          l => String(l).toLowerCase() === String(lbl).toLowerCase()
        );
        const col = Array.isArray(dset0.backgroundColor) ? dset0.backgroundColor[idx] : dset0.backgroundColor;
        return col || '#e84e0e';
      };

      const details = collectDonutDetails(donutDetailSource); // {baja:{units,alarmsTotal,rows}, ...}

      const marginX2 = 40;
      const gapX2 = 12;
      const bottomMargin2 = 40;

      let y = (pdf.lastAutoTable?.finalY || (dY + dImgH + 24)) + 18;

      const detailRowsPerTable = 30;
      const detailMaxColsPerPage = 3;
      const detailGroupGapY = 12;
      const detailGapBetweenCatsY = 16;

      const estimateTableHeight = (rows) => 18 + rows * 12 + 8;
      const minNeededHeight = 14 + estimateTableHeight(detailRowsPerTable);

      const startNewPage = () => {
        pdf.addPage(); pageStart(titles.donut);
        y = HEADER_H + 28;
      };

      // cálculo total de chunks para progreso (85 → 98)
      const countChunks = (catKey) => Math.ceil(((details?.[catKey]?.rows?.length) || 0) / detailRowsPerTable);
      const totalDetailChunks = (countChunks('baja') + countChunks('media') + countChunks('alta')) || 1;
      let processedDetailChunks = 0;
      const tickDetail = () => {
        processedDetailChunks++;
        const pct = 85 + Math.round((processedDetailChunks / totalDetailChunks) * 13);
        onProgress?.(Math.min(pct, 98));
      };

      const renderCategory = (catKey, catTitle) => {
        const meta = details[catKey];
        if (!meta) return;

        const col = forceOpaqueAndMaybeLighten(colorForLabel(catTitle.split(' ')[1] || catTitle), darkModePDF);

        if (y + minNeededHeight > pageH - bottomMargin2) startNewPage();

        // Encabezado
        pdf.setFont('helvetica','bold'); pdf.setFontSize(12);
        setTextColorSafe(pdf, col);
        pdf.text(`${catTitle} — Alarmas: ${meta.alarmsTotal}`, marginX2, y);
        y += 8;

        // filas: [tasa, unidad calculada, jaulas]
        const bodyAllFixed = meta.rows || [];

        const chunks = [];
        for (let i = 0; i < bodyAllFixed.length; i += detailRowsPerTable) {
          chunks.push(bodyAllFixed.slice(i, i + detailRowsPerTable));
        }

        let remaining = [...chunks];

        while (remaining.length) {
          const colsThis = Math.min(detailMaxColsPerPage, remaining.length);
          const totalW = pageW - marginX2 * 2 - gapX2 * (colsThis - 1);
          const tableW = totalW / colsThis;

          if (y + estimateTableHeight(detailRowsPerTable) > pageH - bottomMargin2) {
            startNewPage();
            pdf.setFont('helvetica','bold'); pdf.setFontSize(12);
            setTextColorSafe(pdf, col);
            pdf.text(`${catTitle} — Alarmas: ${meta.alarmsTotal}`, marginX2, y);
            y += 8;
          }

          const colBottomYs = [];
          let x = marginX2;

          for (let c = 0; c < colsThis; c++) {
            const body = remaining.shift();

            autoTable(pdf, {
              head: [['Tasa', 'Unidad', 'Jaulas']],
              body,
              startY: y + 6,
              margin: { left: x, right: marginX2, top: 0, bottom: 0 },
              tableWidth: tableW,
              theme: 'grid',
              styles: {
                fontSize: 8.5, halign: 'center', valign: 'middle',
                lineWidth: 0.25, lineColor: darkModePDF ? DARK_BORDER : [0,0,0],
                textColor: darkModePDF ? [255,255,255] : [0,0,0],
                fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY,
                cellPadding: { top: 2, bottom: 2 },
              },
              columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 70 }, 2: { cellWidth: tableW - 140 } },
              headStyles: { fillColor: ORANGE, textColor: [255,255,255], halign: 'center' },
              bodyStyles: { fillColor: darkModePDF ? DARK_BODY : LIGHT_BODY },
              alternateRowStyles: { fillColor: darkModePDF ? DARK_ALT : LIGHT_ALT },
            });

            colBottomYs.push(pdf.lastAutoTable.finalY);

            // progreso proporcional detalle
            tickDetail();

            x += tableW + gapX2;
          }

          y = Math.max(...colBottomYs) + detailGroupGapY;
        }

        y += detailGapBetweenCatsY;
      };

      renderCategory('baja',  'Tasa Baja');
      renderCategory('media', 'Tasa Media');
      renderCategory('alta',  'Tasa Alta');
    }

    /* 7) Numeración */
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
      setTextColorSafe(pdf, text);
      pdf.text(`${i} / ${totalPages}`, pageW - 40, pageH - 24, { align: 'right' });
    }

    onProgress?.(100);
    pdf.save(fileName);
  } catch (err) {
    console.error("Error en exportSummaryPDF:", err);
    onProgress?.(100); // fuerza cierre del loader en caso de error
  }
}
